import { useState, useRef, useCallback, useEffect } from 'react';
import { getSquarespaceOrders } from '../services/squarespace';
import { logError } from '../utils/errorHandler';

/**
 * Hook for fetching and managing Squarespace online orders.
 *
 * @param {Object} options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @returns {Object} - orders, loading, error, hasMore, fetchOrders, fetchNextPage, clearError
 */
const useSquarespaceOrders = ({ autoFetch = true } = {}) => {
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(autoFetch);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError]               = useState(null);
  const [nextCursor, setNextCursor]     = useState(null);
  const [hasMore, setHasMore]           = useState(false);

  // Track whether we've successfully fetched at least once so subsequent
  // calls show the "refreshing" spinner instead of the full-page loader.
  const hasFetchedRef = useRef(false);

  const fetchOrders = useCallback(async (filters = {}) => {
    try {
      setError(null);

      if (hasFetchedRef.current) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const data = await getSquarespaceOrders(filters);
      hasFetchedRef.current = true;

      setOrders(data.orders || []);
      setNextCursor(data.next_cursor || null);
      setHasMore(data.has_more || false);
    } catch (err) {
      logError('Fetch Squarespace orders', err);
      setError(err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []); // stable — hasFetchedRef is a ref, not reactive state

  const fetchNextPage = useCallback(async (filters = {}) => {
    if (!nextCursor) return;
    try {
      setError(null);
      setIsRefreshing(true);

      const data = await getSquarespaceOrders({ ...filters, cursor: nextCursor });

      setOrders(prev => [...prev, ...(data.orders || [])]);
      setNextCursor(data.next_cursor || null);
      setHasMore(data.has_more || false);
    } catch (err) {
      logError('Fetch next Squarespace page', err);
      setError(err);
    } finally {
      setIsRefreshing(false);
    }
  }, [nextCursor]);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (autoFetch) {
      fetchOrders();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    orders,
    loading,
    isRefreshing,
    error,
    hasMore,
    fetchOrders,
    fetchNextPage,
    clearError,
  };
};

export default useSquarespaceOrders;
